/*
 * Echo-path calibration helper for NkfAecEffect.
 *
 * Plays a short voice clip through the SAME audio path that AEC will see at
 * runtime (HTMLMediaElement → MediaElementAudioSourceNode → AudioContext.destination),
 * captures the mic via sync-aec-worklet so mic and far stay sample-aligned on
 * the AC clock, then GCC-PHATs the two streams to recover the round-trip
 * delay (in samples @ 16 kHz).
 *
 * The whole calibration is done in the browser — no server endpoint required.
 *
 *   import { calibrate } from './calibrator.js';
 *
 *   const result = await calibrate({
 *       voiceUrl: '/libs/nkf-aec/calibration_voice.wav',
 *       workletUrl: '/libs/nkf-aec/sync-aec-worklet.js',
 *   });
 *   console.log(result.delaySamples, result.peakFloorRatio);
 */

const TARGET_SR = 16000;

// ─── Generic radix-2 complex FFT (in-place) ────────────────────────────────
// nFft must be a power of two.
function nextPow2(n) {
    let p = 1;
    while (p < n) p <<= 1;
    return p;
}

function bitReverseTable(n) {
    const tab = new Uint32Array(n);
    let j = 0;
    for (let i = 0; i < n; i++) {
        tab[i] = j;
        let bit = n >> 1;
        while (bit && (j & bit)) { j ^= bit; bit >>= 1; }
        j ^= bit;
    }
    return tab;
}

function fftInPlace(re, im, n, brTab, twiddleC, twiddleS, inverse) {
    // Bit-reversal permutation
    for (let i = 0; i < n; i++) {
        const j = brTab[i];
        if (j > i) {
            let t = re[i]; re[i] = re[j]; re[j] = t;
            t = im[i]; im[i] = im[j]; im[j] = t;
        }
    }
    const sign = inverse ? 1 : -1;
    for (let size = 2; size <= n; size <<= 1) {
        const half = size >> 1;
        const tStep = n / size;
        for (let i = 0; i < n; i += size) {
            for (let k = 0; k < half; k++) {
                const tIdx = k * tStep;
                const c = twiddleC[tIdx];
                const s = sign * twiddleS[tIdx];
                const xR = re[i + k + half], xI = im[i + k + half];
                const tR = c * xR - s * xI;
                const tI = s * xR + c * xI;
                re[i + k + half] = re[i + k] - tR;
                im[i + k + half] = im[i + k] - tI;
                re[i + k] += tR;
                im[i + k] += tI;
            }
        }
    }
    if (inverse) {
        const inv = 1 / n;
        for (let i = 0; i < n; i++) { re[i] *= inv; im[i] *= inv; }
    }
}

/** GCC-PHAT(template, signal): integer lag k where signal[k:] best aligns
 *  with template[0:].  Both are real-valued Float64Array of equal length OK
 *  (we zero-pad to the same nFft).  Returns { lag, peakFloorRatio }. */
export function gccPhat(template, signal, maxLag) {
    const n = nextPow2(template.length + signal.length);
    const brTab = bitReverseTable(n);
    const tCos = new Float64Array(n / 2);
    const tSin = new Float64Array(n / 2);
    for (let i = 0; i < n / 2; i++) {
        tCos[i] = Math.cos(-2 * Math.PI * i / n);
        tSin[i] = Math.sin(-2 * Math.PI * i / n);
    }

    const tRe = new Float64Array(n), tIm = new Float64Array(n);
    const sRe = new Float64Array(n), sIm = new Float64Array(n);
    for (let i = 0; i < template.length; i++) tRe[i] = template[i];
    for (let i = 0; i < signal.length;   i++) sRe[i] = signal[i];

    fftInPlace(tRe, tIm, n, brTab, tCos, tSin, false);
    fftInPlace(sRe, sIm, n, brTab, tCos, tSin, false);

    // R = S · conj(T), then normalize by magnitude (PHAT).
    const rRe = new Float64Array(n), rIm = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        const a = sRe[i], b = sIm[i];
        const c = tRe[i], d = -tIm[i];
        const re = a * c - b * d;
        const im = a * d + b * c;
        const mag = Math.sqrt(re * re + im * im) + 1e-15;
        rRe[i] = re / mag;
        rIm[i] = im / mag;
    }

    fftInPlace(rRe, rIm, n, brTab, tCos, tSin, true);
    // For PHAT-normalised cross-correlation with circular padding, lags
    // 0..signal.length-1 are all valid (signal[k] correlates with template[0]).
    // Cap the search to the user-supplied maxLag so we don't pick a wrap-around.
    const cap = Math.min(maxLag, signal.length);
    if (cap <= 0) return { lag: 0, peakFloorRatio: 0 };
    let bestLag = 0, bestVal = -Infinity;
    let absSum = 0;
    for (let k = 0; k < cap; k++) {
        const v = rRe[k];
        absSum += Math.abs(v);
        if (v > bestVal) { bestVal = v; bestLag = k; }
    }
    const floor = absSum / cap;
    return { lag: bestLag, peakFloorRatio: bestVal / (floor + 1e-12) };
}

/**
 * @param {object} opts
 * @param {string} opts.voiceUrl    URL of the calibration audio (16 kHz mono
 *                                  recommended; any sample rate works since
 *                                  we resample on capture).
 * @param {string} opts.workletUrl  URL of sync-aec-worklet.js.
 * @param {MediaStream} [opts.micStream] Optional existing mic MediaStream
 *                                  to reuse (e.g. localAudioTrack.getOriginalStream()
 *                                  in jitsi).  When omitted, calibrate()
 *                                  opens its own getUserMedia, which fights
 *                                  the conference for the device on some
 *                                  browsers.
 * @returns {Promise<{ delaySamples: number, delayMs: number,
 *                     peakFloorRatio: number, sampleCount: number }>}
 */
export async function calibrate({ voiceUrl, workletUrl, micStream }) {
    if (!voiceUrl)   throw new Error('voiceUrl required');
    if (!workletUrl) throw new Error('workletUrl required');

    let stream = micStream;
    let ownsStream = false;

    if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        });
        ownsStream = true;
    }
    const ac = new AudioContext();
    if (ac.state === 'suspended') await ac.resume();

    try {
        await ac.audioWorklet.addModule(workletUrl);
        const node = new AudioWorkletNode(ac, 'sync-aec', {
            numberOfInputs: 2, numberOfOutputs: 1, outputChannelCount: [ 2 ]
        });

        const audio = new Audio(voiceUrl);
        audio.crossOrigin = 'anonymous';
        audio.preload = 'auto';
        await new Promise((res, rej) => {
            audio.addEventListener('canplaythrough', res, { once: true });
            audio.addEventListener('error', rej, { once: true });
            audio.load();
        });

        const farSrc = ac.createMediaElementSource(audio);
        const micSrc = ac.createMediaStreamSource(stream);
        micSrc.connect(node, 0, 0);
        farSrc.connect(node, 0, 1);
        node.connect(ac.destination);

        const micChunks = [], farChunks = [];
        node.port.onmessage = (ev) => {
            if (ev.data?.__diag) return;        // skip diagnostic messages
            micChunks.push(ev.data.mic);
            farChunks.push(ev.data.far);
        };

        await audio.play();
        const dur = audio.duration || 1.5;
        await new Promise(r => setTimeout(r, (dur + 0.4) * 1000));

        // Only stop tracks we created ourselves — never tear down a stream
        // that was loaned to us (e.g. the active jitsi mic).
        if (ownsStream) {
            stream.getTracks().forEach(t => t.stop());
        }
        await new Promise(r => setTimeout(r, 80));

        // Concat to equal-length Int16 → Float64.
        let totalMic = 0, totalFar = 0;
        for (const m of micChunks) totalMic += m.length;
        for (const f of farChunks) totalFar += f.length;
        const n = Math.min(totalMic, totalFar);
        const mic = new Float64Array(n), far = new Float64Array(n);
        let off = 0;
        for (const m of micChunks) {
            const k = Math.min(m.length, n - off); if (k <= 0) break;
            for (let i = 0; i < k; i++) mic[off + i] = m[i] / 32768;
            off += k;
        }
        off = 0;
        for (const f of farChunks) {
            const k = Math.min(f.length, n - off); if (k <= 0) break;
            for (let i = 0; i < k; i++) far[off + i] = f[i] / 32768;
            off += k;
        }

        // GCC-PHAT(template = far, signal = mic): peak at the round-trip delay.
        const maxLag = Math.min(Math.floor(0.5 * TARGET_SR), n);
        const { lag, peakFloorRatio } = gccPhat(far, mic, maxLag);
        return {
            delaySamples: lag,
            delayMs: lag * 1000 / TARGET_SR,
            peakFloorRatio,
            sampleCount: n,
        };
    } finally {
        try { ac.close(); } catch {}
    }
}
